import gsap from 'gsap'

const KEYWORDS = [
    'AI 工作室',
    'AI 副驾',
    'AI 学习路径',
    'AI 创意工坊'
]

const TYPE_SPEED   = 80   // ms per char
const ERASE_SPEED  = 40
const HOLD_MS      = 1400

export default class Intro
{
    constructor()
    {
        this.overlay = document.querySelector('.intro-overlay')
        this.card    = this.overlay?.querySelector('.intro-card')
        this.tilt    = this.overlay?.querySelector('.intro-tilt')
        this.word    = this.overlay?.querySelector('.intro-rotator__word')
        this.curtain = this.overlay?.querySelector('.intro-curtain')
        this.path    = this.overlay?.querySelector('.intro-curtain__path')

        this._idx = 0
        this._parallax = { tx: 0, ty: 0, rx: 0, ry: 0, target: { tx: 0, ty: 0, rx: 0, ry: 0 } }
        this._disposed = false

        if (!this.overlay) return

        this._startTypewriter()
        this._startParallax()
    }

    // ──────────────────────────────────────────────────────────── typewriter
    _startTypewriter()
    {
        if (!this.word) return
        const tick = async () => {
            if (this._disposed) return
            // erase current
            const current = this.word.textContent
            for (let i = current.length; i >= 0; i--) {
                if (this._disposed) return
                this.word.textContent = current.slice(0, i)
                await wait(ERASE_SPEED)
            }
            // advance
            this._idx = (this._idx + 1) % KEYWORDS.length
            const next = KEYWORDS[this._idx]
            for (let i = 1; i <= next.length; i++) {
                if (this._disposed) return
                this.word.textContent = next.slice(0, i)
                await wait(TYPE_SPEED)
            }
            await wait(HOLD_MS)
            tick()
        }
        // initial hold then begin
        setTimeout(tick, HOLD_MS)
    }

    // ─────────────────────────────────────────────────────────────── parallax
    _startParallax()
    {
        if (!this.tilt) return
        const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
        const isTouch = matchMedia('(hover: none), (pointer: coarse)').matches
        // Skip parallax on touch devices — the 3D transform on the wrapping
        // .intro-tilt can break tap hit-testing on some mobile browsers.
        if (reduced || isTouch) return

        const rect = () => this.tilt.getBoundingClientRect()

        this._onPointer = (e) => {
            const r = rect()
            const cx = r.left + r.width  / 2
            const cy = r.top  + r.height / 2
            const nx = (e.clientX - cx) / (r.width  / 2)
            const ny = (e.clientY - cy) / (r.height / 2)
            this._parallax.target.tx = nx * 4           // px
            this._parallax.target.ty = ny * 3
            this._parallax.target.ry = nx * 2           // deg
            this._parallax.target.rx = -ny * 1.5
        }
        this._onLeave = () => {
            this._parallax.target = { tx: 0, ty: 0, rx: 0, ry: 0 }
        }
        window.addEventListener('pointermove', this._onPointer)
        window.addEventListener('pointerleave', this._onLeave)

        const smooth = 0.12
        const frame = () => {
            if (this._disposed) return
            const p = this._parallax, t = p.target
            p.tx += (t.tx - p.tx) * smooth
            p.ty += (t.ty - p.ty) * smooth
            p.rx += (t.rx - p.rx) * smooth
            p.ry += (t.ry - p.ry) * smooth
            this.tilt.style.transform =
                `translate3d(${p.tx.toFixed(2)}px, ${p.ty.toFixed(2)}px, 0) ` +
                `rotateX(${p.rx.toFixed(2)}deg) rotateY(${p.ry.toFixed(2)}deg)`
            this._raf = requestAnimationFrame(frame)
        }
        this._raf = requestAnimationFrame(frame)
    }

    // ─────────────────────────────────────────────────── curve slide-up exit
    dismiss()
    {
        return new Promise(resolve => {
            if (!this.overlay) { resolve(); return }
            // Curve reveal: path morphs from bulged → flat, then the curtain slides up
            // with the card fading concurrently.
            if (this.curtain && this.path) {
                this.curtain.classList.add('is-showing')
                // pull curtain up (which reveals the scene underneath) while card fades
                const tl = gsap.timeline({
                    onComplete: () => {
                        this.overlay.hidden = true
                        this.curtain.classList.remove('is-showing')
                        this._dispose()
                        resolve()
                    }
                })
                tl.fromTo(this.path,
                    { attr: { d: 'M0 0 L1000 0 L1000 1000 Q500 1000 0 1000 Z' } },
                    { attr: { d: 'M0 0 L1000 0 L1000 1000 Q500 1200 0 1000 Z' },
                      duration: 0.35, ease: 'power2.in' })
                tl.to(this.card, { opacity: 0, y: -30, duration: 0.4, ease: 'power2.in' }, 0)
                tl.to(this.curtain, {
                    y: '-110%', duration: 0.8, ease: 'cubic-bezier(0.76,0,0.24,1)'
                }, 0.2)
                tl.to(this.overlay, { opacity: 0, duration: 0.3 }, 0.5)
            } else {
                gsap.to(this.overlay, {
                    opacity: 0, duration: 0.45, ease: 'power2.out',
                    onComplete: () => { this.overlay.hidden = true; this._dispose(); resolve() }
                })
            }
        })
    }

    _dispose()
    {
        this._disposed = true
        if (this._raf) cancelAnimationFrame(this._raf)
        if (this._onPointer) window.removeEventListener('pointermove', this._onPointer)
        if (this._onLeave)   window.removeEventListener('pointerleave', this._onLeave)
    }
}

const wait = (ms) => new Promise(r => setTimeout(r, ms))
